/**
 * Reed.ai Design System — Button
 *
 * Variants:  Primary | Secondary | Tertiary | Link | Destructive
 * States:    Default | Hover | Focus | Inactive
 * Themes:    Light | Dark
 * Sizes:     Desktop | Mobile
 * Layout:    Text only | Icon + Text | Text + Icon
 *
 * Source: Figma "Reed.ai - Global components" — A / Buttons & links page
 * Nodes:  651:3973 (primary), 651:4119 (secondary), 651:4265 (tertiary), 651:4527 (link),
 *         9993:55 (destructive, default) / 9993:67 (destructive, hover)
 */

import React from 'react';
import { cn } from '../../utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ButtonVariant  = 'primary' | 'secondary' | 'tertiary' | 'link' | 'destructive';
export type ButtonState    = 'default' | 'hover' | 'focus' | 'inactive';
export type ButtonTheme    = 'light' | 'dark';
export type ButtonLayout   = 'text-only' | 'icon-text' | 'text-icon';
export type ButtonSize     = 'desktop' | 'mobile';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style of the button */
  variant?: ButtonVariant;
  /** Light or dark surface context */
  theme?: ButtonTheme;
  /** Icon position relative to label */
  layout?: ButtonLayout;
  /** Desktop uses the default button scale, mobile uses compact typography */
  size?: ButtonSize;
  /** Label text shown inside the button. If `children` is also provided, children win. */
  label?: string;
  /** Content inside the button. Wins over `label` when both are present. */
  children?: React.ReactNode;
  /** Optional icon element */
  icon?: React.ReactNode;
  /** Convenience shorthand — maps to disabled + inactive styling */
  inactive?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Token-backed brand gradient value for consumers that need the canonical button fill. */
export const BRAND_GRADIENT =
  'linear-gradient(150deg, var(--colour-brand-dark-carnaby-pink) 20.4%, var(--colour-brand-carnaby-pink) 116.3%)';

// No `overflow-hidden` here: it would cancel the flex-item minimum size
// (min-width:auto), letting flex-1/w-full consumers squash the button below
// its content and hard-clip the nowrap label (FED item 3, issue #556). The
// hover overlay clips itself via before:rounded-full instead.
const buttonBase = [
  'relative inline-flex items-center justify-center rounded-full',
  'h-12 min-w-26 gap-2 whitespace-nowrap',
  'font-body text-body-md font-medium',
  'select-none transition-colors duration-150',
  'focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-[3px]',
];

const layoutPadding: Record<ButtonLayout, string> = {
  'text-only': 'px-6 py-3',
  'icon-text': 'pl-4 pr-6 py-3',
  'text-icon': 'pl-6 pr-4 py-3',
};

function contentOf(
  children: React.ReactNode,
  label: string | undefined,
  fallback: string,
  ariaLabel?: string,
) {
  // Icon-only buttons (aria-label, no visible text) must not render the
  // literal fallback string.
  return children ?? label ?? (ariaLabel ? null : fallback);
}

/**
 * An explicit `layout` wins; otherwise the presence of `icon` implies
 * icon-text so the prop is never silently dropped (issue #556).
 */
function resolveLayout(layout: ButtonLayout | undefined, icon: React.ReactNode): ButtonLayout {
  if (layout) return layout;
  return icon ? 'icon-text' : 'text-only';
}

function iconSlot(icon: React.ReactNode, className?: string) {
  return (
    <span className={cn('relative z-10 flex size-6 shrink-0 items-center justify-center', className)}>
      {icon}
    </span>
  );
}

/** Chevron/arrow icon used for tertiary Text+Chevron variant */
function ChevronRight({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={cn('relative z-10 shrink-0', className)}
      fill="none"
      height="24"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </svg>
  );
}

/** External-link / arrow-right icon used for link buttons */
function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={cn('relative z-10 shrink-0', className)}
      fill="none"
      height="24"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
  );
}

function ButtonContents({
  content,
  icon,
  layout,
  fallbackIcon,
  iconClassName,
}: {
  content: React.ReactNode;
  icon?: React.ReactNode;
  layout: ButtonLayout;
  fallbackIcon?: React.ReactNode;
  /** Extra classes on the icon slot — a variant may tint the icon independently of the label. */
  iconClassName?: string | undefined;
}) {
  const trailingIcon = icon ? iconSlot(icon, iconClassName) : fallbackIcon;

  return (
    <>
      {layout === 'icon-text' && icon && iconSlot(icon, iconClassName)}
      {content != null && <span className="relative z-10">{content}</span>}
      {layout === 'text-icon' && trailingIcon}
    </>
  );
}

// ─── Primary Button ───────────────────────────────────────────────────────────

function PrimaryButton({
  label,
  children,
  icon,
  layout: layoutProp,
  size = 'desktop',
  theme = 'light',
  inactive,
  className,
  disabled,
  style,
  ...rest
}: ButtonProps) {
  const isInactive = inactive || disabled;
  const ariaLabel = (rest as Record<string, unknown>)['aria-label'] as string | undefined;
  const layout = resolveLayout(layoutProp, icon);
  const content = contentOf(children, label, 'Button', ariaLabel);

  return (
    <button
      {...rest}
      aria-disabled={isInactive || rest['aria-disabled']}
      className={cn(
        buttonBase,
        'focus-visible:outline-brand-dark-carnaby-pink',
        layoutPadding[layout],
        size === 'mobile' && 'text-body-md-m',
        isInactive
          ? [
              'cursor-not-allowed bg-surface-primary-inactive',
              theme === 'dark' ? 'text-neutral-70 opacity-30' : 'text-text-secondary',
            ]
          : [
              'cursor-pointer text-brand-white',
              'before:absolute before:inset-0 before:rounded-full before:bg-neutral-90 before:opacity-0 before:transition-opacity',
              'hover:before:opacity-20',
            ],
        className,
      )}
      disabled={!!isInactive}
      style={{ ...(!isInactive ? { backgroundImage: BRAND_GRADIENT } : {}), ...style }}
    >
      <ButtonContents content={content} icon={icon} layout={layout} />
    </button>
  );
}

// ─── Secondary Button ─────────────────────────────────────────────────────────

function SecondaryButton({
  label,
  children,
  icon,
  layout: layoutProp,
  size = 'desktop',
  theme = 'light',
  inactive,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  const isInactive = inactive || disabled;
  const ariaLabel = (rest as Record<string, unknown>)['aria-label'] as string | undefined;
  const layout = resolveLayout(layoutProp, icon);
  const content = contentOf(children, label, 'Button', ariaLabel);

  return (
    <button
      {...rest}
      aria-disabled={isInactive || rest['aria-disabled']}
      className={cn(
        buttonBase,
        'focus-visible:outline-border-secondary',
        layoutPadding[layout],
        size === 'mobile' && 'text-body-md-m',
        isInactive
          ? [
              'cursor-not-allowed bg-neutral-10',
              theme === 'dark' ? 'text-neutral-70 opacity-30' : 'text-text-secondary',
            ]
          : [
              'cursor-pointer text-text-primary hover:bg-surface-secondary-hover',
              theme === 'dark' ? 'bg-brand-white' : 'bg-surface-secondary',
            ],
        className,
      )}
      disabled={!!isInactive}
    >
      <ButtonContents content={content} icon={icon} layout={layout} />
    </button>
  );
}

// ─── Tertiary Button ──────────────────────────────────────────────────────────

function TertiaryButton({
  label,
  children,
  icon,
  layout: layoutProp,
  size = 'desktop',
  theme = 'light',
  inactive,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  const isInactive = inactive || disabled;
  const ariaLabel = (rest as Record<string, unknown>)['aria-label'] as string | undefined;
  const layout = resolveLayout(layoutProp, icon);
  const content = contentOf(children, label, 'Button', ariaLabel);
  const isDark = theme === 'dark';
  const inactiveDarkText = layout === 'text-only'
    ? 'text-surface-secondary'
    : layout === 'icon-text'
      ? 'text-border-primary'
      : 'text-neutral-20';

  return (
    <button
      {...rest}
      aria-disabled={isInactive || rest['aria-disabled']}
      className={cn(
        buttonBase,
        'border bg-transparent focus-visible:outline-border-tertiary',
        layoutPadding[layout],
        size === 'mobile' && 'text-body-md-m',
        isInactive
          ? [
              'cursor-not-allowed border-neutral-20',
              isDark ? [inactiveDarkText, 'opacity-30'] : 'bg-brand-white text-text-secondary',
            ]
          : [
              'cursor-pointer hover:border-border-tertiary hover:bg-surface-secondary hover:text-text-primary',
              isDark
                ? 'border-border-secondary/40 text-brand-white'
                : 'border-border-tertiary bg-brand-white text-text-primary',
            ],
        className,
      )}
      disabled={!!isInactive}
    >
      <ButtonContents
        content={content}
        icon={icon}
        layout={layout}
        fallbackIcon={<ChevronRight className="size-6" />}
      />
    </button>
  );
}

// ─── Link Button ──────────────────────────────────────────────────────────────

function LinkButton({
  label,
  children,
  icon,
  layout: layoutProp,
  size = 'desktop',
  theme = 'light',
  inactive,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  const isInactive = inactive || disabled;
  const ariaLabel = (rest as Record<string, unknown>)['aria-label'] as string | undefined;
  // Link icons trail by convention, so a bare `icon` implies text-icon.
  const layout = layoutProp ?? (icon ? 'text-icon' : 'text-only');
  const content = contentOf(children, label, 'Link', ariaLabel);
  const isDark = theme === 'dark';
  const inactiveDarkText = layout === 'text-icon'
    ? ['text-border-primary', 'opacity-30']
    : 'text-neutral-20';

  return (
    <button
      {...rest}
      aria-disabled={isInactive || rest['aria-disabled']}
      className={cn(
        'relative inline-flex items-center justify-center gap-2 whitespace-nowrap bg-transparent p-0',
        'font-body text-body-md-link',
        'select-none transition-colors duration-150',
        'focus-visible:outline focus-visible:outline-1 focus-visible:outline-current',
        size === 'mobile' && 'text-body-md-m font-semibold',
        isInactive
          ? [
              'cursor-not-allowed no-underline',
              isDark ? inactiveDarkText : 'text-text-secondary',
            ]
          : [
              'cursor-pointer decoration-current underline-offset-2',
              layout === 'text-icon' ? 'no-underline hover:underline focus-visible:underline' : 'underline',
              isDark ? 'text-brand-white hover:text-brand-carnaby-pink' : 'text-text-primary hover:text-brand-dark-carnaby-pink',
            ],
        className,
      )}
      disabled={!!isInactive}
    >
      {content != null && <span>{content}</span>}
      {layout === 'text-icon' && (icon ? iconSlot(icon) : <ArrowIcon />)}
    </button>
  );
}

// ─── Destructive Button ───────────────────────────────────────────────────────

/**
 * Outlined error-red pill for irreversible or clearing actions ("Remove
 * candidate"). Figma 9993:55 (default) / 9993:67 (hover): white surface, 1px
 * error-red border, piccadilly label, error-red icon; hover fills
 * error-red-light. Figma draws it at the compact 28px card-CTA scale — keep the
 * shared 48px base geometry here so it lines up with the other variants in
 * dialog footers, and let compact consumers pass `className` overrides
 * (`h-7 px-4 py-1 gap-1.5`), which `cn` merges cleanly. Inactive and dark
 * surface are not designed: inactive mirrors tertiary, dark renders the light
 * treatment.
 */
function DestructiveButton({
  label,
  children,
  icon,
  layout: layoutProp,
  size = 'desktop',
  theme: _theme,
  inactive,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  const isInactive = inactive || disabled;
  const ariaLabel = (rest as Record<string, unknown>)['aria-label'] as string | undefined;
  const layout = resolveLayout(layoutProp, icon);
  const content = contentOf(children, label, 'Button', ariaLabel);

  return (
    <button
      {...rest}
      aria-disabled={isInactive || rest['aria-disabled']}
      className={cn(
        buttonBase,
        'border bg-brand-white focus-visible:outline-border-error',
        layoutPadding[layout],
        size === 'mobile' && 'text-body-md-m',
        isInactive
          ? 'cursor-not-allowed border-neutral-20 text-text-secondary'
          : 'cursor-pointer border-border-error text-text-primary hover:bg-surface-error-container',
        className,
      )}
      disabled={!!isInactive}
    >
      <ButtonContents
        content={content}
        icon={icon}
        layout={layout}
        iconClassName={isInactive ? undefined : 'text-text-error'}
      />
    </button>
  );
}

// ─── Main Button component ────────────────────────────────────────────────────

/**
 * Reed.ai Button — unified component that delegates to the correct variant.
 *
 * @example
 * // Primary CTA
 * <Button variant="primary" label="Save changes" />
 *
 * // Secondary with icon
 * <Button variant="secondary" layout="icon-text" icon={<PlusIcon />} label="Add role" />
 *
 * // Tertiary with chevron
 * <Button variant="tertiary" layout="text-icon" label="View all" />
 *
 * // Link
 * <Button variant="link" label="Learn more" layout="text-icon" />
 *
 * // Destructive (outlined error red, icon tinted red)
 * <Button variant="destructive" icon={<Icon name="Trash" size={16} />} label="Remove candidate" />
 *
 * // Dark theme
 * <Button variant="primary" theme="dark" label="Get started" />
 *
 * // Inactive / disabled
 * <Button variant="primary" inactive label="Unavailable" />
 */
export function Button({ variant = 'primary', ...props }: ButtonProps) {
  switch (variant) {
    case 'secondary': return <SecondaryButton {...props} />;
    case 'tertiary':  return <TertiaryButton  {...props} />;
    case 'link':      return <LinkButton      {...props} />;
    case 'destructive': return <DestructiveButton {...props} />;
    default:          return <PrimaryButton   {...props} />;
  }
}

export default Button;
